"use client";

import { useMemo } from "react";
import { type Option, SelectField } from "@/components/tool-kit/primitives";
import { useToolNumber } from "@/components/tools/tool-ui";
import {
  CURRENCY_CODES,
  FREQUENCY_VALUES,
  type FrequencyKey,
  moneyOptions,
  PLAIN,
} from "@/lib/tool-kit/finance/currency";
import { CONTINUOUS } from "@/lib/tool-kit/finance/tvm";

/** Writes an amount of money the reader's way, in the selected currency. */
export function useMoney(currency: string): (value: number) => string {
  const fmt = useToolNumber();
  return useMemo(() => {
    const options = moneyOptions(currency);
    return (value: number) => fmt(value, options);
  }, [fmt, currency]);
}

/** Writes a rate that is held as a decimal as a percentage with up to `max` decimals. */
export function usePercent(
  max = 4
): (decimal: number, maxDecimals?: number) => string {
  const fmt = useToolNumber();
  return (decimal, maxDecimals = max) =>
    fmt(decimal * 100, { maximumFractionDigits: maxDecimals });
}

export function CurrencyField({
  id,
  label,
  value,
  onChange,
  plainLabel,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The label for "no currency — plain numbers". */
  plainLabel: string;
}) {
  const options: Option[] = [
    ...CURRENCY_CODES.map((code) => ({ value: code, label: code })),
    { value: PLAIN, label: plainLabel },
  ];
  return (
    <SelectField
      id={id}
      label={label}
      onChange={onChange}
      options={options}
      value={value}
    />
  );
}

/** Every frequency name a widget's strings must supply. */
export type FrequencyLabels = Record<FrequencyKey | "continuous", string>;

/** Select options for the frequencies in `keys`; the value is periods per year. */
export function frequencyOptions(
  labels: FrequencyLabels,
  keys: readonly FrequencyKey[],
  continuous = false
): Option[] {
  const options: Option[] = keys.map((key) => ({
    value: String(FREQUENCY_VALUES[key]),
    label: labels[key],
  }));
  if (continuous) {
    options.push({ value: "continuous", label: labels.continuous });
  }
  return options;
}

/** Periods per year from a frequency select value. */
export function perYearOf(value: string): number {
  return value === "continuous" ? CONTINUOUS : Number(value);
}

/** Compounding choices, longest period first. */
export const COMPOUNDING_KEYS: readonly FrequencyKey[] = [
  "annually",
  "semiannually",
  "quarterly",
  "monthly",
  "weekly",
  "daily",
];

/** Regular deposit or payment schedules. */
export const SCHEDULE_KEYS: readonly FrequencyKey[] = [
  "annually",
  "semiannually",
  "quarterly",
  "monthly",
  "semimonthly",
  "biweekly",
  "weekly",
];
